import { observer } from 'mobx-react-lite';
import { selectEventCalendarLabel } from './CalendarSelector.locstring.json';
import { FontClassNames } from '@uifabric/styling/lib/index';
import { CommandButton, IButton, IButtonProps } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon } from '@fluentui/react/lib/Icon';
import {
    Calendar,
    getCalendarEntryByFolderId,
    getCalendarIdByFolderId,
    isCalendarInMailbox,
    isWritableCalendar,
    getAllCalendars,
    isTeamsCalendarEntry,
} from 'owa-calendar-cache';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { onCalendarIdChanged } from 'owa-calendar-compose-form-actions';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import getCalendarOwnerFromItem from 'owa-calendar-forms-common/lib/utils/getCalendarOwnerFromItem';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { getPaletteAsRawColors } from 'owa-theme';
import * as React from 'react';

let styles = require('./CalendarSelector.scss');
let classNames = require('classnames/bind').bind(styles);

/**
 * Props for calendar selector
 */
export interface CalendarSelectorProps extends React.HTMLProps<HTMLDivElement> {
    /** dropdown color scheme */
    colorScheme: ColorScheme;
}

/**
 * Component uses fabric command button with menuProps (creates a contextualMenu) to select a calendar.
 */
export default observer(function CalendarSelector(props: CalendarSelectorProps) {
    const calendarDropdown = React.useRef<IButton>();
    // This is used to fix a callout dismissal bug in IE11 with React 16 and Fabric Contextual Menus
    const onItemSelectedWasCalled = React.useRef<boolean>(false);
    const focus = () => {
        calendarDropdown.current.focus();
    };
    const bindCalendarDropdownRef = ref => (calendarDropdown.current = ref);
    /**
     *  Create the options for the calendar dropdown from the given calendars
     *  @return calendarMenuItems of type IContextualMenuItem[]
     */
    const createMenuCalendarOptions = () => {
        const event = getComposeStore().event;
        const selectedCalendarId = getCalendarIdByFolderId(event.ParentFolderId.Id);
        // if event is not new then get only the writable calendars from the same mailbox, otherwise get all writable calendars
        const mailboxOwner = event.ItemId ? getCalendarOwnerFromItem(event).EmailAddress : null;
        const { colorScheme } = props;
        return getAllCalendars()
            .filter(
                cal =>
                    isWritableCalendar(cal) &&
                    !isTeamsCalendarEntry(cal) &&
                    (!mailboxOwner || isCalendarInMailbox(cal, mailboxOwner))
            )
            .map(cal => {
                const isSelected = selectedCalendarId && cal.id.id == selectedCalendarId.id;
                return {
                    key: cal.id.id,
                    name: cal.displayName,
                    checked: isSelected,
                    canCheck: true,
                    iconProps: {
                        iconName: ControlIcons.CircleFill,
                        className: styles.menuItemResultIcon,
                        style: {
                            color: getCalendarColorScheme(cal.id).calPrimary,
                        },
                    },
                    data: { calendar: cal },
                    style: isSelected
                        ? {
                              backgroundColor: getOptimalTextColorString(
                                  getPaletteAsRawColors().neutralPrimary,
                                  colorScheme,
                                  colorScheme.calLighter
                              ),
                          }
                        : null,
                    className: classNames(FontClassNames.medium, styles.menuItemResultText),
                };
            });
    };
    /**
     * Callback issued when the menu item is invoked
     */
    const onItemClick = (ev?: React.MouseEvent<HTMLElement>, item?: IContextualMenuItem) => {
        onCalendarIdChanged((item.data.calendar as Calendar).id);
        onItemSelectedWasCalled.current = true;
    };
    /**
     * Callback issued when the menu is dimissed to ensure the focus goes back to the command button
     */
    const onAfterMenuDismissPreventIE11React16CalloutDismiss = () => {
        if (onItemSelectedWasCalled.current) {
            focus();
            onItemSelectedWasCalled.current = false;
        }
    };
    /**
     * Renders the icon within the command button
     */
    const onRenderMenuIcon = (): JSX.Element => {
        return (
            <Icon
                iconName={ControlIcons.ChevronDown}
                className={styles.commandButtonIcon}
                style={props.style}
            />
        );
    };
    const onRenderText = (props: IButtonProps, onRenderText: () => JSX.Element) => (
        <div className={styles.calendarLabel} style={props.style} title={props.text}>
            {onRenderText()}
        </div>
    );
    const { colorScheme, ...divProps } = props;
    /* Stores all the users calendar options */
    const calendarMenuItems: IContextualMenuItem[] = createMenuCalendarOptions();
    const isOneCalendarItem = calendarMenuItems.length == 1;
    const selectedCalendarDisplayName = getCalendarEntryByFolderId(
        getComposeStore().event.ParentFolderId.Id
    ).CalendarName;
    const buttonStyle = {
        root: {
            color: colorScheme.accessibleText.calPrimaryBackground + '!important',
        },
        rootExpanded: {
            color: colorScheme.accessibleText.calPrimaryBackground,
        },
    };
    return (
        <div {...(divProps as React.HTMLAttributes<HTMLDivElement>)}>
            <CommandButton
                componentRef={bindCalendarDropdownRef}
                className={classNames({
                    commandButton: true,
                    singleCalendar: isOneCalendarItem,
                })}
                text={selectedCalendarDisplayName}
                menuProps={
                    !isOneCalendarItem && {
                        gapSpace: 8,
                        items: calendarMenuItems,
                        onItemClick: onItemClick,
                    }
                }
                onRenderMenuIcon={!isOneCalendarItem && onRenderMenuIcon}
                onAfterMenuDismiss={onAfterMenuDismissPreventIE11React16CalloutDismiss}
                ariaDescription={loc(selectEventCalendarLabel)}
                onRenderText={onRenderText}
                styles={buttonStyle}
            />
        </div>
    );
});
